import { useState, useMemo } from 'react'
import { BaseModal, Button, InputOTP, Wizard, useWizardFooter, renderWizardFooter, type WizardStep } from '@airclou/ui'
import { CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface TwoFactorSetupModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  /**
   * Proves the code against the server and returns the backup codes.
   * Rejects with an Error carrying `.code` for a named failure.
   */
  onVerify: (code: string) => Promise<string[]>
  /** A `data:` URL rendered by the auth service, not a third-party image. */
  qrCodeUrl: string
  secretKey: string
  /** Shown once, after activation. Nothing can recover them afterwards. */
  backupCodes: string[]
  userEmail: string
}

export function TwoFactorSetupModal({
  isOpen,
  onClose,
  onComplete,
  onVerify,
  qrCodeUrl,
  secretKey,
  backupCodes,
  userEmail: _userEmail,
}: TwoFactorSetupModalProps) {
  const { t } = useTranslation('dashboard')
  const [step, setStep] = useState(1)
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [backupCodeCopied, setBackupCodeCopied] = useState(false)

  const handleCancel = () => {
    onClose()
    // Reset state
    setStep(1)
    setVerificationCode('')
    setError('')
    setBackupCodeCopied(false)
  }

  const handleComplete = () => {
    onComplete()
    // Reset state for next time
    setStep(1)
    setVerificationCode('')
    setError('')
    setBackupCodeCopied(false)
  }

  // Wizard steps
  const wizardSteps = useMemo<WizardStep[]>(() => [
    {
      id: 'qr-code',
      title: t('twoFactor.scanQR'),
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('twoFactor.scanQRTitle')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('twoFactor.scanQRDescription')}
            </p>
          </div>

          <div className="flex justify-center">
            {/* Stays white in dark mode too: the QR PNG is black-on-white and
                scanners need the light quiet zone around it. */}
            {/* eslint-disable-next-line airclou/no-hardcoded-theme-colors -- see above: bg-card here breaks scanning in dark mode */}
            <div className="p-4 bg-white rounded-lg border border-border">
              <img 
                src={qrCodeUrl} 
                alt="QR Code for 2FA setup"
                className="w-48 h-48"
              />
            </div>
          </div>

          {/* The same secret in typeable form. Not a nicety: a QR code is
              unusable to anyone enrolling a desktop authenticator, reading the
              screen the code is displayed on, or without a working camera —
              without this, enrolment is simply closed to them. The enrol
              endpoint returns the secret for exactly this reason. */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('twoFactor.orEnterManually')}</p>
            <code className="inline-block px-3 py-2 bg-muted border border-border rounded text-sm font-mono tracking-wider break-all">
              {secretKey}
            </code>
          </div>
        </div>
      )
    },
    {
      id: 'verify',
      title: t('twoFactor.verify'),
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('twoFactor.enterVerificationCode')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('twoFactor.enterCodeDescription')}
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex justify-center">
              <InputOTP
                value={verificationCode}
                onChange={(value) => {
                  setVerificationCode(value)
                  setError('')
                }}
                disabled={isVerifying}
                numInputs={6}
              />
            </div>

            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                {t('twoFactor.backspaceHint')}
              </p>
            </div>
          </div>
        </div>
      ),
      validate: async () => {
        if (verificationCode.length !== 6) {
          setError(t('twoFactor.invalidCode'))
          return false
        }

        setIsVerifying(true)
        setError('')

        try {
          // The real check. This used to accept any six characters — and a
          // dummy list of codes before that — so "verified" meant nothing and
          // the wizard would happily report success for 000000.
          await onVerify(verificationCode)
          return true
        } catch (err) {
          const code = (err as { code?: string }).code
          setError(
            code === 'INVALID_CODE' ? t('twoFactor.invalidVerificationCode')
            : code === 'TOO_MANY_ATTEMPTS' ? t('twoFactor.tooManyAttempts')
            : code === 'NO_PENDING_ENROLLMENT' ? t('twoFactor.enrollmentExpired')
            : t('twoFactor.verificationFailed'),
          )
          return false
        } finally {
          setIsVerifying(false)
        }
      }
    },
    {
      id: 'complete',
      title: t('twoFactor.complete'),
      content: (
        <div className="space-y-6 text-center">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('twoFactor.enabledTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('twoFactor.enabledDescription')}
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              {t('twoFactor.saveBackupCode')}
            </h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
              {t('twoFactor.saveBackupCodeDescription')}
            </p>
            {/* All of them, not one. The server issues ten and each is
                single-use, so showing a single code would leave nine the user
                never saw — and this is the only time any of them exist in
                readable form. */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {backupCodes.map(code => (
                <code
                  key={code}
                  className="px-3 py-2 bg-yellow-100 dark:bg-yellow-900/40 border border-yellow-200 dark:border-yellow-800 rounded text-sm font-mono text-center text-yellow-900 dark:text-yellow-100"
                >
                  {code}
                </code>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(backupCodes.join('\n'))
                setBackupCodeCopied(true)
                setTimeout(() => setBackupCodeCopied(false), 2000)
              }}
              className="w-full"
            >
              {backupCodeCopied ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                t('twoFactor.copyAll')
              )}
            </Button>
          </div>
        </div>
      )
    }
  ], [qrCodeUrl, secretKey, verificationCode, isVerifying, backupCodes, backupCodeCopied])

  // Get wizard footer props
  const wizardFooterProps = useWizardFooter({
    steps: wizardSteps,
    currentStep: step,
    onStepChange: setStep,
    onComplete: handleComplete,
    onCancel: handleCancel,
    isLoading: isVerifying,
    // Never. The flag reads as "can go back FROM the current step", so the old
    // `step !== 2` put a Back button on step 3 — the one step where going back
    // is destructive. By then the factor is already active and the backup codes
    // are on screen for the only time they will ever be readable; Back navigates
    // off them, and going forward again re-runs the verifier against an
    // enrolment the server has already consumed, stranding the user on step 2
    // with an error and no codes. Step 1 has nothing to go back to, and step 2
    // was already excluded, so false loses nothing — and where the flag is
    // false the footer offers Cancel instead, which re-reads the real status.
    canGoBack: false,
    nextButtonText: step === 1 ? t('twoFactor.addedAccount') : t('twoFactor.verifyAndEnable'),
    completeButtonText: t('twoFactor.completeSetup'),
    cancelButtonText: t('common.cancel'),
    backButtonText: t('common.back')
  })

  const getTitle = () => {
    switch (step) {
      case 1:
        return t('twoFactor.setupTitle')
      case 2:
        return t('twoFactor.enterVerificationCode')
      case 3:
        return t('twoFactor.setupCompleteTitle')
      default:
        return t('security.twoFactor')
    }
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={step === 2 ? () => {} : handleCancel}
      title={getTitle()}
      size="lg"
      closeOnBackdrop={step !== 2}
      showCloseButton={step !== 2}
      footer={renderWizardFooter(wizardFooterProps)}
      zIndex={99999}
    >
      <Wizard
        steps={wizardSteps}
        currentStep={step}
        onStepChange={setStep}
        onComplete={handleComplete}
        onCancel={handleCancel}
        isLoading={isVerifying}
        showStepIndicator={true}
        errorMessage={error}
        externalFooter={true}
      />
    </BaseModal>
  )
}
